import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DOOSP_INFO, PROGRAMS } from "@/lib/programs";

const NAV_LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/programs", label: "Programs", hasDropdown: true },
  { to: "/certifications", label: "Certifications" },
  { to: "/resources", label: "Resources" },
  { to: "/news", label: "News" },
  { to: "/corporate", label: "Corporate Training" },
  { to: "/contact", label: "Contact" },
];

export default function DoospNav() {
  const { user } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [programsOpen, setProgramsOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); setProgramsOpen(false); }, [location.pathname]);

  return (
    <header className="border-b border-zinc-200 bg-white sticky top-0 z-40" data-testid="doosp-nav">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center group" data-testid="doosp-logo">
            <img
              src="/doosp_logo.png"
              alt="IMCAC — Department of Occupational Safety Programs"
              className="h-10 md:h-11 w-auto object-contain group-hover:opacity-90 transition"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) =>
              link.hasDropdown ? (
                <div key={link.to} className="relative"
                     onMouseEnter={() => setProgramsOpen(true)}
                     onMouseLeave={() => setProgramsOpen(false)}>
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `px-3 py-2 text-sm font-medium inline-flex items-center gap-1 rounded-md transition ${
                        isActive ? "text-red-800" : "text-zinc-700 hover:text-red-800"
                      }`
                    }
                    data-testid={`nav-${link.label.toLowerCase()}`}
                  >
                    {link.label} <ChevronDown className="h-3.5 w-3.5" />
                  </NavLink>
                  {programsOpen && (
                    <div className="absolute top-full left-0 pt-2 z-50" data-testid="programs-dropdown">
                      <div className="bg-white border border-zinc-200 shadow-xl rounded-lg py-2 w-72">
                        {PROGRAMS.map((p) => (
                          <Link
                            key={p.slug}
                            to={`/programs/${p.slug}`}
                            className="flex items-start gap-2 px-4 py-2.5 hover:bg-zinc-50 transition"
                          >
                            <div className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 bg-${p.color}-500`} />
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">{p.name}</div>
                              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                                {p.status === "live" ? "Live · certification open" : "Coming soon"}
                              </div>
                            </div>
                          </Link>
                        ))}
                        <div className="border-t border-zinc-100 mt-2 pt-2 px-4 py-1">
                          <Link to="/programs" className="text-xs text-red-700 hover:text-red-900 font-mono uppercase tracking-widest">
                            View all programs →
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium rounded-md transition ${
                      isActive ? "text-red-800" : "text-zinc-700 hover:text-red-800"
                    }`
                  }
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard" className="hidden md:inline-flex items-center px-4 h-9 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-medium" data-testid="nav-dashboard">
                My Dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" className="hidden md:inline-flex text-sm text-zinc-700 hover:text-red-800 font-medium" data-testid="nav-login">
                  Log in
                </Link>
                <Link to="/register" className="hidden md:inline-flex items-center px-4 h-9 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-medium" data-testid="nav-register">
                  Get certified
                </Link>
              </>
            )}
            <button className="lg:hidden text-zinc-700" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu" data-testid="nav-mobile-toggle">
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-zinc-100 py-4 space-y-1" data-testid="nav-mobile-menu">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) =>
                  `block px-3 py-2 text-sm font-medium rounded-md ${
                    isActive ? "text-red-800 bg-red-50" : "text-zinc-700 hover:bg-zinc-50"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <div className="border-t border-zinc-100 mt-4 pt-4 space-y-2">
              {user ? (
                <Link to="/dashboard" className="block px-3 py-2 rounded-md bg-red-600 text-white text-sm font-medium text-center">My Dashboard</Link>
              ) : (
                <>
                  <Link to="/login" className="block px-3 py-2 text-sm text-zinc-700">Log in</Link>
                  <Link to="/register" className="block px-3 py-2 rounded-md bg-red-600 text-white text-sm font-medium text-center">Get certified</Link>
                </>
              )}
            </div>
            <div className="border-t border-zinc-100 pt-4 mt-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 px-3 mb-2">Programs</div>
              {PROGRAMS.map((p) => (
                <Link key={p.slug} to={`/programs/${p.slug}`} className="block px-3 py-1.5 text-sm text-zinc-700 hover:text-red-800">
                  {p.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
